/**
 * R24 Lightbox — vanilla dialog + GSAP, replaces SimpleLightbox.
 * Triggers: .gallery-card, .herogallery-asset-link, figure a[href]
 * Caption: data-lightbox-caption (HTML from r24_gallery_lightbox_caption)
 */
import { gsap } from 'gsap';

const SWIPE_THRESHOLD = 48;
const FADE_DURATION = 0.33;

const TRIGGER_SELECTOR =
  'a.gallery-card[href], a.herogallery-asset-link[href], figure a[href]';

const PREV_ICON =
  '<svg class="r24-lightbox__nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z" clip-rule="evenodd"/></svg>';

const NEXT_ICON =
  '<svg class="r24-lightbox__nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z" clip-rule="evenodd"/></svg>';

const CLOSE_ICON =
  '<svg class="r24-lightbox__close-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/></svg>';

/** @type {HTMLDialogElement | null} */
let dialog = null;
/** @type {HTMLElement | null} */
let panel = null;
/** @type {HTMLElement | null} */
let imgWrapEl = null;
/** @type {HTMLImageElement | null} */
let imgEl = null;
/** @type {HTMLElement | null} */
let mediaEl = null;
/** @type {HTMLElement | null} */
let captionEl = null;
/** @type {HTMLButtonElement | null} */
let btnPrev = null;
/** @type {HTMLButtonElement | null} */
let btnNext = null;
/** @type {HTMLButtonElement | null} */
let btnClose = null;
/** @type {HTMLElement | null} */
let counterEl = null;

/** @type {HTMLAnchorElement[]} */
let currentGroup = [];
let currentIndex = 0;
/** @type {HTMLAnchorElement | null} */
let lastTrigger = null;
let isOpen = false;
let downX = null;
/** @type {(() => void) | null} */
let resizeHandler = null;
let hashPushedToHistory = false;
let hashHistoryActive = false;

const prefersReduced =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {number} index
 * @param {number} total
 */
function formatCounter(index, total) {
  const pad = Math.max(2, String(total).length);
  const cur = String(index + 1).padStart(pad, '0');
  const tot = String(total).padStart(pad, '0');
  return `${cur}/${tot}`;
}

/**
 * @param {HTMLAnchorElement} link
 * @returns {HTMLAnchorElement[]}
 */
function getGroupForLink(link) {
  const rel = link.getAttribute('rel');
  if (!rel || rel === 'false' || rel === 'nofollow') {
    return [link];
  }
  return [...document.querySelectorAll(TRIGGER_SELECTOR)].filter(
    (el) => el.getAttribute('rel') === rel
  );
}

/**
 * @param {HTMLAnchorElement} link
 * @returns {number} 1-based flat index across all page triggers
 */
function getFlatPidForLink(link) {
  const elements = document.querySelectorAll(TRIGGER_SELECTOR);
  const index = [...elements].indexOf(link);
  return index >= 0 ? index + 1 : 1;
}

/**
 * @param {HTMLAnchorElement} link
 */
function syncLightboxHash(link) {
  const pid = getFlatPidForLink(link);
  const newHash = `#pid=${pid}`;
  if (window.location.hash === newHash) {
    return;
  }

  const newURL = `${window.location.href.split('#')[0]}${newHash}`;
  if (!hashHistoryActive) {
    history.pushState(null, document.title, newURL);
    hashPushedToHistory = true;
    hashHistoryActive = true;
  } else {
    history.replaceState(null, document.title, newURL);
  }
}

function resetLightboxHash() {
  if (hashPushedToHistory) {
    hashPushedToHistory = false;
    hashHistoryActive = false;
    history.back();
    return;
  }

  hashHistoryActive = false;
  if (window.location.hash.startsWith('#pid=')) {
    history.replaceState(
      null,
      document.title,
      `${window.location.pathname}${window.location.search}`
    );
  }
}

/**
 * @param {string} html
 * @param {HTMLElement} imgWrap
 * @param {HTMLElement} caption
 */
function applyCaption(html, imgWrap, caption) {
  caption.innerHTML = '';
  imgWrap.querySelectorAll('.figure-credit-chips').forEach((el) => el.remove());
  caption.hidden = true;

  const trimmed = (html || '').trim();
  if (!trimmed) {
    return;
  }

  const wrap = document.createElement('div');
  wrap.innerHTML = trimmed;

  const textEl = wrap.querySelector('.lightbox-caption-text');
  const chipsEl = wrap.querySelector('.figure-credit-chips');

  if (textEl) {
    caption.appendChild(textEl.cloneNode(true));
    caption.hidden = false;
  } else if (!chipsEl) {
    caption.innerHTML = trimmed;
    caption.hidden = false;
  }

  if (chipsEl) {
    imgWrap.appendChild(chipsEl.cloneNode(true));
  }
}

function resetImageWrap() {
  if (!imgWrapEl) {
    return;
  }
  imgWrapEl.style.width = '';
  imgWrapEl.style.height = '';
}

/** Size the image wrap to the letterboxed display box so credit chips sit on the photo. */
function fitImageWrap() {
  if (!imgEl || !imgWrapEl || !mediaEl) {
    return;
  }

  resetImageWrap();

  const mw = mediaEl.clientWidth;
  const mh = mediaEl.clientHeight;
  const nw = imgEl.naturalWidth;
  const nh = imgEl.naturalHeight;

  if (!mw || !mh || !nw || !nh) {
    return;
  }

  const scale = Math.min(mw / nw, mh / nh);
  imgWrapEl.style.width = `${Math.floor(nw * scale)}px`;
  imgWrapEl.style.height = `${Math.floor(nh * scale)}px`;
}

function bindResize() {
  if (resizeHandler) {
    return;
  }
  resizeHandler = () => {
    if (isOpen) {
      fitImageWrap();
    }
  };
  window.addEventListener('resize', resizeHandler);
}

function unbindResize() {
  if (!resizeHandler) {
    return;
  }
  window.removeEventListener('resize', resizeHandler);
  resizeHandler = null;
}

function preloadAdjacent() {
  [-1, 1].forEach((delta) => {
    const i = currentIndex + delta;
    if (i >= 0 && i < currentGroup.length) {
      const src = currentGroup[i].href;
      if (src) {
        const preload = new Image();
        preload.src = src;
      }
    }
  });
}

function updateNav() {
  const total = currentGroup.length;
  const multi = total > 1;

  if (counterEl) {
    counterEl.hidden = !multi;
    if (multi) {
      counterEl.textContent = formatCounter(currentIndex, total);
    }
  }

  if (btnPrev) {
    btnPrev.hidden = !multi || currentIndex === 0;
  }
  if (btnNext) {
    btnNext.hidden = !multi || currentIndex >= total - 1;
  }
}

/**
 * @param {number} index
 * @param {{ skipHash?: boolean }} [options]
 */
function showSlide(index, options = {}) {
  if (!imgEl || !imgWrapEl || !captionEl) {
    return;
  }

  const link = currentGroup[index];
  if (!link) {
    return;
  }

  currentIndex = index;
  const src = link.getAttribute('href') || '';
  const captionHtml = link.getAttribute('data-lightbox-caption') || '';

  imgEl.src = src;
  imgEl.alt =
    link.getAttribute('aria-label') ||
    link.getAttribute('title') ||
    link.querySelector('img')?.getAttribute('alt') ||
    '';

  applyCaption(captionHtml, imgWrapEl, captionEl);
  updateNav();
  preloadAdjacent();

  if (!options.skipHash && isOpen) {
    syncLightboxHash(link);
  }

  const onLoad = () => {
    fitImageWrap();
    imgEl?.removeEventListener('load', onLoad);
  };

  imgEl.addEventListener('load', onLoad);
  if (imgEl.complete && imgEl.naturalWidth > 0) {
    fitImageWrap();
  }
}

function goTo(delta) {
  const next = currentIndex + delta;
  if (next < 0 || next >= currentGroup.length) {
    return;
  }
  showSlide(next);
}

function buildDialog() {
  dialog = document.createElement('dialog');
  dialog.className = 'r24-lightbox';
  dialog.setAttribute('aria-label', 'Image viewer');
  dialog.setAttribute('tabindex', '-1');

  dialog.innerHTML = `
    <div class="r24-lightbox__panel">
      <button type="button" class="r24-lightbox__close" aria-label="Close">${CLOSE_ICON}</button>
      <div class="r24-lightbox__counter" aria-hidden="true" hidden></div>
      <button type="button" class="r24-lightbox__nav r24-lightbox__nav--prev" aria-label="Previous image" hidden>${PREV_ICON}</button>
      <button type="button" class="r24-lightbox__nav r24-lightbox__nav--next" aria-label="Next image" hidden>${NEXT_ICON}</button>
      <figure class="r24-lightbox__figure">
        <div class="r24-lightbox__media">
          <div class="r24-lightbox__img-wrap">
            <img class="r24-lightbox__img" alt="">
          </div>
        </div>
        <figcaption class="r24-lightbox__caption" hidden></figcaption>
      </figure>
    </div>
  `;

  document.body.appendChild(dialog);

  panel = dialog.querySelector('.r24-lightbox__panel');
  imgWrapEl = dialog.querySelector('.r24-lightbox__img-wrap');
  imgEl = dialog.querySelector('.r24-lightbox__img');
  mediaEl = dialog.querySelector('.r24-lightbox__media');
  captionEl = dialog.querySelector('.r24-lightbox__caption');
  btnPrev = dialog.querySelector('.r24-lightbox__nav--prev');
  btnNext = dialog.querySelector('.r24-lightbox__nav--next');
  btnClose = dialog.querySelector('.r24-lightbox__close');
  counterEl = dialog.querySelector('.r24-lightbox__counter');

  btnClose?.addEventListener('click', () => closeLightbox());
  btnPrev?.addEventListener('click', () => goTo(-1));
  btnNext?.addEventListener('click', () => goTo(1));

  // Figure is nearly full-viewport; only the img-wrap is the "content" — letterbox
  // padding inside .r24-lightbox__media should still close the lightbox.
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) {
      closeLightbox();
    }
  });

  panel?.addEventListener('click', (e) => {
    if (e.target === panel) {
      closeLightbox();
    }
  });

  mediaEl?.addEventListener('click', (e) => {
    if (e.target === mediaEl) {
      closeLightbox();
    }
  });

  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    closeLightbox();
  });

  dialog.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeLightbox();
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goTo(-1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goTo(1);
    }
  });

  mediaEl?.addEventListener(
    'pointerdown',
    (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) {
        return;
      }
      downX = e.clientX;
      try {
        mediaEl.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    { passive: true }
  );

  mediaEl?.addEventListener(
    'pointerup',
    (e) => {
      if (downX === null) {
        return;
      }
      const dx = e.clientX - downX;
      downX = null;
      try {
        mediaEl.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (Math.abs(dx) < SWIPE_THRESHOLD) {
        return;
      }
      if (dx < 0) {
        goTo(1);
      } else {
        goTo(-1);
      }
    },
    { passive: true }
  );

  mediaEl?.addEventListener('pointercancel', () => {
    downX = null;
  });
}

/**
 * @param {HTMLAnchorElement} link
 */
function openLightbox(link) {
  if (!dialog || !panel) {
    return;
  }

  currentGroup = getGroupForLink(link);
  currentIndex = Math.max(0, currentGroup.indexOf(link));
  lastTrigger = link;
  isOpen = true;

  showSlide(currentIndex);
  bindResize();

  if (typeof window.lockScroll === 'function') {
    window.lockScroll();
  }

  dialog.showModal();

  if (prefersReduced) {
    gsap.set(panel, { autoAlpha: 1 });
  } else {
    gsap.fromTo(panel, { autoAlpha: 0 }, { autoAlpha: 1, duration: FADE_DURATION, ease: 'power2.out' });
  }

  requestAnimationFrame(() => {
    dialog?.focus({ preventScroll: true });
    fitImageWrap();
  });
}

function closeLightbox(options = {}) {
  if (!dialog || !panel || !isOpen) {
    return;
  }

  isOpen = false;

  if (!options.skipHashReset) {
    resetLightboxHash();
  } else {
    hashPushedToHistory = false;
    hashHistoryActive = false;
  }

  const finish = () => {
    unbindResize();
    resetImageWrap();
    dialog.close();
    if (typeof window.unlockScroll === 'function') {
      window.unlockScroll();
    }
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
    lastTrigger = null;
    currentGroup = [];
    if (imgEl) {
      imgEl.removeAttribute('src');
    }
  };

  if (prefersReduced) {
    finish();
    return;
  }

  gsap.to(panel, {
    autoAlpha: 0,
    duration: FADE_DURATION * 0.85,
    ease: 'power2.in',
    onComplete: finish,
  });
}

/**
 * @param {Event} e
 */
function onTriggerClick(e) {
  const target = e.target;
  if (!(target instanceof Element)) {
    return;
  }

  const link = target.closest(TRIGGER_SELECTOR);
  if (!(link instanceof HTMLAnchorElement) || !link.href) {
    return;
  }

  e.preventDefault();
  openLightbox(link);
}

/**
 * Open by flat page index (#pid=N), matching legacy SimpleLightbox behaviour.
 * @param {number} flatIndex 0-based index across all triggers on the page
 */
export function openLightboxByFlatIndex(flatIndex) {
  const elements = document.querySelectorAll(TRIGGER_SELECTOR);
  const link = elements[flatIndex];
  if (link instanceof HTMLAnchorElement) {
    openLightbox(link);
  }
}

function onHashChange() {
  if (!isOpen) {
    return;
  }

  const hash = window.location.hash;
  if (!hash.startsWith('#pid=')) {
    closeLightbox({ skipHashReset: true });
    return;
  }

  const pidMatch = hash.match(/#pid=(\d+)/);
  if (!pidMatch) {
    return;
  }

  const flatIndex = parseInt(pidMatch[1], 10) - 1;
  const elements = document.querySelectorAll(TRIGGER_SELECTOR);
  const link = elements[flatIndex];
  if (!(link instanceof HTMLAnchorElement)) {
    return;
  }

  if (currentGroup[currentIndex] === link) {
    return;
  }

  currentGroup = getGroupForLink(link);
  currentIndex = Math.max(0, currentGroup.indexOf(link));
  showSlide(currentIndex, { skipHash: true });
}

export function initMetaLightbox() {
  if (!document.querySelector(TRIGGER_SELECTOR)) {
    return;
  }

  buildDialog();
  document.addEventListener('click', onTriggerClick);
  window.addEventListener('hashchange', onHashChange);

  if (window.location.hash && window.location.hash.startsWith('#pid=')) {
    hashHistoryActive = true;
    const pidMatch = window.location.hash.match(/#pid=(\d+)/);
    if (pidMatch) {
      const targetIndex = parseInt(pidMatch[1], 10) - 1;
      setTimeout(() => {
        openLightboxByFlatIndex(targetIndex);
      }, 100);
    }
  }
}
